import { customElement, DeesElement, state, html, css, cssManager, domtools, type TemplateResult } from '@design.estate/dees-element';
import type { DeesInputText, TFormData, ITableAction, Column } from '@design.estate/dees-catalog';
import '@design.estate/dees-catalog';
import type * as interfaces from '../../ts/ffb.forges.interfaces.js';

type TFormEvent = CustomEvent<{ data: TFormData }>;
const providers = [{ key: 'gitea', option: 'Gitea' }, { key: 'gitlab', option: 'GitLab' }];
const modes = [
  { key: 'oneWaySync', option: 'One-way sync' }, { key: 'twoWaySync', option: 'Two-way sync' },
  { key: 'mirror', option: 'Mirror' }, { key: 'migration', option: 'Migration' },
];
const selection = (value: unknown): string => value && typeof value === 'object' && 'key' in value ? String(value.key) : '';
const text = (value: unknown): string => typeof value === 'string' ? value : '';

@customElement('dotglobal-forges')
export class DotglobalForges extends DeesElement {
  @state() private accessor authenticated = false;
  @state() private accessor busy = false;
  @state() private accessor message = '';
  @state() private accessor connections: interfaces.IForgeConnection[] = [];
  @state() private accessor drafts: interfaces.IForgeTransferDraft[] = [];
  @state() private accessor editingConnection: interfaces.IForgeConnection | null | undefined;
  @state() private accessor editingDraft: interfaces.IForgeTransferDraft | null | undefined;
  @state() private accessor draftMode: interfaces.TForgeTransferMode = 'oneWaySync';
  @state() private accessor draftEndpointIds: [string, string] = ['', ''];
  @state() private accessor draftSourceSide: '0' | '1' = '0';
  @state() private accessor mappingRows: Array<[string, string]> = [['', '']];
  @state() private accessor probeResult: interfaces.IForgeConnectionProbe | undefined;
  #accessToken = '';
  #epoch = 0;
  #requestAbort?: AbortController;
  #newInstanceId = crypto.randomUUID();
  #newConfigId = crypto.randomUUID();

  public static styles = [cssManager.defaultStyles, css`
    :host { display: block; color: ${cssManager.bdTheme('#18212f', '#e5e7eb')}; }
    .page { max-width: 1180px; margin: 0 auto; padding: 28px 20px; }
    h1 { font-size: 28px; margin: 0 0 8px; }
    h2 { font-size: 18px; margin: 0 0 14px; }
    p { line-height: 1.5; }
    .intro, .help { color: ${cssManager.bdTheme('#5b6676', '#a3adbb')}; }
    .panel { padding: 22px; border: 1px solid ${cssManager.bdTheme('#dce2e9', '#374151')}; border-radius: 12px; margin: 20px 0; }
    .login { max-width: 520px; }
    .grid, .mapping { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .mapping { align-items: end; margin: 12px 0; }
    .actions { display: flex; flex-wrap: wrap; gap: 10px; margin: 16px 0; }
    .notice { padding: 12px 16px; background: ${cssManager.bdTheme('#eef4fc', '#172539')}; border-radius: 8px; }
    dees-table { display: block; margin: 24px 0; }
    dees-input-text, dees-input-dropdown, dees-input-checkbox { display: block; margin-bottom: 12px; }
    @media (max-width: 720px) { .grid, .mapping { grid-template-columns: 1fr; gap: 4px; } .page { padding: 20px 12px; } }
  `];

  public async disconnectedCallback(): Promise<void> {
    this.lock();
    await super.disconnectedCallback();
  }

  public render(): TemplateResult {
    return html`<div class="page">
      <h1>Forge connections</h1>
      <p class="intro">Connect your Gitea and GitLab instances, then choose how repositories and issues should move between them.</p>
      ${this.message ? html`<p class="notice" role="status">${this.message}</p>` : ''}
      ${!this.authenticated ? html`
        <section class="panel login"><h2>Operator access</h2>
          <p>Use your forge-management token to manage connections. It stays in this tab’s memory.</p>
          <dees-form @formData=${(event: TFormEvent) => this.login(event)}>
            <dees-input-text key="operatorToken" label="Operator token" .isPasswordBool=${true} autocomplete="off" .required=${true}></dees-input-text>
            <dees-form-submit text="Open connections" .disabled=${this.busy}></dees-form-submit>
          </dees-form>
        </section>` : html`
        <div class="actions"><dees-button @clicked=${() => this.lock()}>Lock access</dees-button></div>
        <p class="help">Connections can be used on either side of a transfer. GitHub support is pending.</p>
        <dees-table heading1="Instances" heading2=""
          .data=${this.connections} .columns=${this.connectionColumns} .dataActions=${this.connectionActions()}></dees-table>
        ${this.editingConnection !== undefined ? this.connectionForm() : ''}
        ${this.probeResult ? html`<p class="notice">Read probe: ${this.probeResult.repositoryRead === 'available' ? 'repository endpoint available' : 'unverified'}.
          ${this.probeResult.serverVersion ? `Server ${this.probeResult.serverVersion}.` : 'Server version unverified.'}
          ${this.probeResult.reason ?? ''} This does not verify complete inventory or write permissions.</p>` : ''}
        <p class="help">Saving a draft does not start a transfer. Inventory, permissions and preservation checks are required before activation.</p>
        <dees-table heading1="Drafts" heading2=""
          .data=${this.drafts} .columns=${this.draftColumns} .dataActions=${this.draftActions()}></dees-table>
        ${this.editingDraft !== undefined ? this.draftForm() : ''}
      `}
    </div>`;
  }

  private readonly connectionColumns: Column<interfaces.IForgeConnection>[] = [
    { key: 'label', header: 'Name' }, { key: 'kind', header: 'Platform' }, { key: 'baseUrl', header: 'Instance URL' },
    { key: 'disabled', header: 'Status', value: (row) => row.disabled ? 'Disabled' : 'Configured' },
    { key: 'credentialStatus', header: 'Credential' },
  ];
  private readonly draftColumns: Column<interfaces.IForgeTransferDraft>[] = [
    { key: 'label', header: 'Name' }, { key: 'mode', header: 'Mode', value: (row) => modes.find((mode) => mode.key === row.mode)?.option ?? row.mode },
    { key: 'endpointInstanceIds', header: 'Direction', value: (row) => {
      const ids = row.sourceInstanceId === row.endpointInstanceIds[1] ? [...row.endpointInstanceIds].reverse() : row.endpointInstanceIds;
      return ids.map((id) => this.connections.find((connection) => connection.instanceId === id)?.label ?? id).join(row.mode === 'twoWaySync' ? ' ↔ ' : ' → ');
    } },
    { key: 'includeIssues', header: 'Issues', value: (row) => row.includeIssues ? 'Included' : 'Excluded' },
    { key: 'state', header: 'State' },
  ];

  private connectionActions(): ITableAction<interfaces.IForgeConnection>[] {
    return [
      { name: 'Refresh', iconName: 'lucide:RefreshCw', type: ['header'], actionFunc: async () => this.refresh() },
      { name: 'Add', iconName: 'lucide:Plus', type: ['header'], actionFunc: async () => this.editConnection(null) },
      { name: 'Edit', iconName: 'lucide:Pencil', type: ['inRow', 'contextmenu', 'doubleClick'], actionFunc: async ({ item }) => this.editConnection(item) },
      { name: 'Test read access', iconName: 'lucide:Plug', type: ['inRow', 'contextmenu'], actionRelevancyCheckFunc: (row) => !row.disabled && row.credentialStatus === 'configured' && !this.busy,
        actionFunc: async ({ item }) => this.perform((accessToken, signal) => new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_ForgeConnectionProbe>('/typedrequest', 'forgeConnectionProbe').fire({ accessToken, instanceId: item.instanceId, expectedRevision: item.revision }, this.requestOptions(signal)),
          (result) => { this.probeResult = result.probe; }) },
    ];
  }
  private draftActions(): ITableAction<interfaces.IForgeTransferDraft>[] {
    return [
      { name: 'New draft', iconName: 'lucide:Plus', type: ['header'], actionFunc: async () => this.editTransfer(null) },
      { name: 'Edit draft', iconName: 'lucide:Pencil', type: ['inRow', 'contextmenu', 'doubleClick'], actionFunc: async ({ item }) => this.editTransfer(item) },
    ];
  }

  private connectionForm(): TemplateResult {
    const current = this.editingConnection;
    return html`<section class="panel"><h2>${current ? 'Edit connection' : 'Add connection'}</h2>
      ${current ? html`<p class="help">${current.baseUrl} · ${current.instanceId}</p>` : ''}
      <dees-form id="connection-form" @formData=${(event: TFormEvent) => this.saveConnection(event)}>
        <dees-input-text key="label" label="Name" .value=${current?.label ?? ''} .required=${true}></dees-input-text>
        ${!current ? html`<div class="grid">
          <dees-input-dropdown key="kind" label="Platform" .options=${providers} .selectedOption=${providers[0]} .required=${true}></dees-input-dropdown>
          <dees-input-text key="baseUrl" label="Instance URL" infoText="HTTPS root, including any deployment subpath" .required=${true}></dees-input-text>
        </div>` : ''}
        <dees-input-text key="providerToken" label=${current ? 'Replacement access token (leave empty to keep)' : 'Provider access token'} .isPasswordBool=${true} autocomplete="off" .required=${!current}></dees-input-text>
        ${current ? html`
          <dees-input-checkbox key="disabled" label="Disable this connection" .value=${current.disabled}></dees-input-checkbox>
          <dees-input-checkbox key="removeCredential" label="Remove stored credential and disable connection"></dees-input-checkbox>
          <p class="help">Credential changes retain this instance’s identity and transfer drafts. Changing the instance URL requires a verified move.</p>` : ''}
        <dees-form-submit text="Save connection" .disabled=${this.busy}></dees-form-submit>
      </dees-form>
      <div class="actions"><dees-button @clicked=${() => { this.clearPasswords(); this.editingConnection = undefined; }}>Cancel</dees-button></div>
    </section>`;
  }

  private draftForm(): TemplateResult {
    const current = this.editingDraft;
    const options = this.connections.map((connection) => ({ key: connection.instanceId, option: `${connection.label} (${connection.kind})` }));
    const first = options.find((option) => option.key === this.draftEndpointIds[0]);
    const second = options.find((option) => option.key === this.draftEndpointIds[1]);
    const direction = [{ key: '0', option: 'First instance → second instance' }, { key: '1', option: 'Second instance → first instance' }];
    return html`<section class="panel"><h2>${current ? 'Edit transfer draft' : 'New transfer draft'}</h2>
      <dees-form id="draft-form" @formData=${(event: TFormEvent) => this.saveTransfer(event)}>
        <dees-input-text key="label" label="Name" .value=${current?.label ?? ''} .required=${true}></dees-input-text>
        <div class="grid">
          <dees-input-dropdown key="first" label="First instance" .options=${options} .selectedOption=${first ?? null} .required=${true}
            @selectedOption=${(event: CustomEvent<{ key: string }>) => { this.draftEndpointIds = [event.detail.key, this.draftEndpointIds[1]]; }}></dees-input-dropdown>
          <dees-input-dropdown key="second" label="Second instance" .options=${options} .selectedOption=${second ?? null} .required=${true}
            @selectedOption=${(event: CustomEvent<{ key: string }>) => { this.draftEndpointIds = [this.draftEndpointIds[0], event.detail.key]; }}></dees-input-dropdown>
        </div>
        <dees-input-dropdown key="mode" label="Mode" .options=${modes} .selectedOption=${modes.find((mode) => mode.key === this.draftMode) ?? modes[0]}
          @selectedOption=${(event: CustomEvent<{ key: interfaces.TForgeTransferMode }>) => { this.draftMode = event.detail.key; }}></dees-input-dropdown>
        ${this.draftMode !== 'twoWaySync' ? html`<dees-input-dropdown key="direction" label="Direction" .options=${direction}
          .selectedOption=${direction[this.draftSourceSide === '1' ? 1 : 0]}
          @selectedOption=${(event: CustomEvent<{ key: '0' | '1' }>) => { this.draftSourceSide = event.detail.key; }}></dees-input-dropdown>` : html`<p class="help">Edits may originate on either side. Competing changes require conflict resolution.</p>`}
        ${this.draftMode === 'mirror' ? html`<p class="help">A mirror follows the selected source, including supported deletions after verification.</p>` : ''}
        <dees-input-checkbox key="includeIssues" label="Include issues" .value=${current?.includeIssues ?? true}></dees-input-checkbox>
        <h2>Repository mappings</h2><p class="help">Enter explicit namespace/repository paths for both instances. GitLab paths may include nested groups.</p>
        ${this.mappingRows.map((row, index) => html`<div class="mapping">
          <dees-input-text key=${`left${index}`} label="Path on first instance" .value=${row[0]} .required=${true}
            @input=${(event: Event) => this.setMapping(index, 0, text((event.target as DeesInputText).value))}></dees-input-text>
          <dees-input-text key=${`right${index}`} label="Path on second instance" .value=${row[1]} .required=${true}
            @input=${(event: Event) => this.setMapping(index, 1, text((event.target as DeesInputText).value))}></dees-input-text>
          ${this.mappingRows.length > 1 ? html`<dees-button @clicked=${() => { this.mappingRows = this.mappingRows.filter((_, rowIndex) => rowIndex !== index); }}>Remove mapping</dees-button>` : ''}
        </div>`)}
        <div class="actions"><dees-button .disabled=${this.mappingRows.length >= 100} @clicked=${() => { this.mappingRows = [...this.mappingRows, ['', '']]; }}>Add mapping</dees-button></div>
        <dees-form-submit text="Save draft" .disabled=${this.busy}></dees-form-submit>
      </dees-form>
      <p class="help">This saves your intended transfer. Replication activation is pending; no repositories or issues are changed.</p>
      <div class="actions"><dees-button @clicked=${() => { this.editingDraft = undefined; }}>Cancel</dees-button></div>
    </section>`;
  }

  private setMapping(index: number, side: 0 | 1, value: string): void {
    this.mappingRows = this.mappingRows.map((row, rowIndex) => rowIndex === index ? side === 0 ? [value, row[1]] : [row[0], value] : row);
  }
  private async editConnection(connection: interfaces.IForgeConnection | null): Promise<void> {
    if (this.busy) return;
    this.clearPasswords(); this.editingConnection = undefined; await this.updateComplete;
    this.editingConnection = connection; this.probeResult = undefined;
    if (!connection) this.#newInstanceId = crypto.randomUUID();
  }
  private async editTransfer(draft: interfaces.IForgeTransferDraft | null): Promise<void> {
    if (this.busy) return;
    if (this.connections.length < 2) { this.message = 'Add two connections before creating a transfer draft.'; return; }
    this.editingDraft = undefined; await this.updateComplete;
    this.editingDraft = draft; this.draftMode = draft?.mode ?? 'oneWaySync';
    this.draftEndpointIds = draft ? [...draft.endpointInstanceIds] : [this.connections[0].instanceId, this.connections[1].instanceId];
    this.draftSourceSide = draft?.sourceInstanceId === draft?.endpointInstanceIds[1] && draft ? '1' : '0';
    this.mappingRows = draft?.repositoryMappings.map((mapping) => [mapping.paths[0].join('/'), mapping.paths[1].join('/')]) ?? [['', '']];
    if (!draft) this.#newConfigId = crypto.randomUUID();
  }
  private async login(event: TFormEvent): Promise<void> {
    event.stopPropagation(); if (this.busy) return;
    this.#accessToken = text(event.detail.data.operatorToken); this.clearPasswords(); await this.refresh();
  }
  private async refresh(): Promise<void> {
    await this.perform(async (accessToken, signal) => {
      const connections = new Map<string, interfaces.IForgeConnection>();
      const drafts = new Map<string, interfaces.IForgeTransferDraft>();
      let afterInstanceId: string | undefined; let afterConfigId: string | undefined;
      for (let page = 0; page < 100; page++) {
        const result = await new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_ForgeConnectionsList>('/typedrequest', 'forgeConnectionsList').fire({ accessToken, afterInstanceId, afterConfigId }, this.requestOptions(signal));
        result.connections.forEach((row) => connections.set(row.instanceId, row)); result.drafts.forEach((row) => drafts.set(row.configId, row));
        if (!result.nextInstanceId && !result.nextConfigId) return { connections: [...connections.values()], drafts: [...drafts.values()] };
        afterInstanceId = result.nextInstanceId ?? afterInstanceId; afterConfigId = result.nextConfigId ?? afterConfigId;
      }
      throw new Error('inventory-limit');
    }, (result) => { this.connections = result.connections; this.drafts = result.drafts; this.authenticated = true; this.probeResult = undefined; });
  }
  private async saveConnection(event: TFormEvent): Promise<void> {
    event.stopPropagation(); const data = event.detail.data;
    const current = this.editingConnection;
    const providerToken = text(data.providerToken);
    const label = text(data.label);
    await this.perform(async (accessToken, signal) => {
      if (current) return new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_ForgeConnectionUpdate>('/typedrequest', 'forgeConnectionUpdate').fire({ accessToken, connection: {
        instanceId: current.instanceId, expectedRevision: current.revision, label,
        disabled: Boolean(data.disabled || data.removeCredential),
        ...(data.removeCredential ? { token: null } : providerToken ? { token: providerToken } : {}),
      } }, this.requestOptions(signal));
      return new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_ForgeConnectionCreate>('/typedrequest', 'forgeConnectionCreate').fire({ accessToken, connection: {
        instanceId: this.#newInstanceId, kind: selection(data.kind) as interfaces.TForgeConnectionKind, label, baseUrl: text(data.baseUrl), token: providerToken,
      } }, this.requestOptions(signal));
    }, (result) => {
      this.connections = [...this.connections.filter((row) => row.instanceId !== result.connection.instanceId), result.connection];
      this.clearPasswords(); this.editingConnection = undefined; this.probeResult = undefined; this.message = 'Connection saved.';
    });
  }
  private async saveTransfer(event: TFormEvent): Promise<void> {
    event.stopPropagation(); const data = event.detail.data; const current = this.editingDraft;
    const endpointInstanceIds: [string, string] = [selection(data.first), selection(data.second)];
    const mode = selection(data.mode) as interfaces.TForgeTransferMode;
    const draft: interfaces.TForgeTransferDraftInput = {
      configId: current?.configId ?? this.#newConfigId, label: text(data.label), endpointInstanceIds, mode,
      sourceInstanceId: mode === 'twoWaySync' ? null : endpointInstanceIds[selection(data.direction) === '1' ? 1 : 0],
      includeIssues: Boolean(data.includeIssues),
      repositoryMappings: this.mappingRows.map((_, index) => ({ paths: [text(data[`left${index}`]).split('/'), text(data[`right${index}`]).split('/')] })),
    };
    await this.perform((accessToken, signal) => new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_ForgeTransferDraftSave>('/typedrequest', 'forgeTransferDraftSave').fire({ accessToken, expectedRevision: current?.revision ?? null, draft }, this.requestOptions(signal)),
      (result) => { this.drafts = [...this.drafts.filter((row) => row.configId !== result.draft.configId), result.draft]; this.editingDraft = undefined; this.message = 'Transfer draft saved. Replication has not started.'; });
  }
  private requestOptions(signalArg: AbortSignal) {
    return { timeoutMs: 30_000, maxRetries: 0, abortSignal: signalArg };
  }
  private async perform<T>(operation: (accessToken: string, signal: AbortSignal) => Promise<T>, apply: (result: T) => void): Promise<void> {
    if (this.busy) return;
    const epoch = this.#epoch; const token = this.#accessToken;
    const controller = new AbortController(); this.#requestAbort = controller;
    this.busy = true; this.message = '';
    try {
      const result = await operation(token, controller.signal);
      if (epoch === this.#epoch) apply(result);
    } catch (error) {
      if (epoch !== this.#epoch) return;
      const message = error instanceof Error ? error.message : '';
      this.message = message.includes('unauthorized') ? 'Operator access was rejected.' : message.includes('conflict') ? 'Settings changed. Refresh and review them before retrying.' : message.includes('invalid-input') ? 'Check the instance details, direction and repository mappings.' : 'Forge management is unavailable or the request failed. Refresh before retrying a change.';
      if (message.includes('unauthorized')) { this.lock(); this.message = 'Operator access was rejected.'; }
    } finally { if (epoch === this.#epoch) { this.busy = false; this.#requestAbort = undefined; } }
  }
  private clearPasswords(): void {
    this.shadowRoot?.querySelectorAll('dees-input-text').forEach((input) => { if (input.isPasswordBool) input.value = ''; });
  }
  private lock(): void {
    this.#requestAbort?.abort(); this.#requestAbort = undefined;
    this.#epoch++; this.#accessToken = ''; this.clearPasswords(); this.authenticated = false; this.busy = false;
    this.connections = []; this.drafts = []; this.editingConnection = undefined; this.editingDraft = undefined; this.probeResult = undefined; this.message = '';
  }
}
